import { useMemo } from "react";
import type { Answers, DashboardSection } from "../../types";

export type ChipTone = "solid" | "plain" | "warn" | "ok";

export interface PackageChip {
  label: string;
  tone: ChipTone;
}

export interface DataRow {
  label: string;
  value: string;
}

export type EffortBand = 1 | 2 | 3 | 4;

export interface RequirementsPackage {
  /** Report name once given, otherwise "Live". */
  title: string;
  /** The dashboard's core question (section 02) — what the whole package is for. */
  purpose: string;
  /** Who it is built for, and what that group is missing today. */
  persona: PackageChip[];
  pages: PackageChip[];
  visuals: PackageChip[];
  sources: PackageChip[];
  dataRows: DataRow[];
  /** Distribution and visibility — one block, since section 07 covers both. */
  delivery: PackageChip[];
  effort: { scored: boolean; band: EffortBand | null; bandName: string; score: number };
}

const BAND_NAMES = ["Basis", "Standard", "Erweitert", "Komplex"];

/** Comma-separated free text → trimmed entries. */
function splitCommas(value: unknown): string[] {
  if (typeof value !== "string") return [];
  return value
    .split(",")
    .map((entry) => entry.trim())
    .filter(Boolean);
}

/** One-per-line free text (KPI list, page list) → trimmed entries, list markers stripped. */
function splitLines(value: unknown): string[] {
  if (typeof value !== "string") return [];
  return value
    .split("\n")
    .map((entry) => entry.replace(/^\s*(?:[-•*]|\d+[.)])\s*/, "").trim())
    .filter(Boolean);
}

function text(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function chip(label: string, tone: ChipTone = "solid"): PackageChip {
  return { label, tone };
}

/**
 * Derives the live Anforderungspaket from the current answers.
 *
 * Every question in the catalog surfaces somewhere here — a control whose
 * answer changes nothing on the right reads as broken. Free text that cannot be
 * chipped meaningfully (Kernfrage, Sonstiges) becomes the panel subtitle or a
 * marker chip rather than being dropped.
 *
 * Chip text comes from the catalog's option labels rather than from literals
 * here — the JSON stays the single source of truth for wording, and renaming an
 * option cannot silently desynchronise the panel from the form.
 */
export function buildRequirementsPackage(
  sections: DashboardSection[],
  answers: Answers
): RequirementsPackage {
  const labelIndex = new Map<string, Map<string, string>>();
  for (const section of sections) {
    for (const question of section.questions) {
      if (!question.options) continue;
      labelIndex.set(question.id, new Map(question.options.map((o) => [o.value, o.label])));
    }
  }

  const single = (id: string): string => (typeof answers[id] === "string" ? (answers[id] as string) : "");
  const multi = (id: string): string[] => (Array.isArray(answers[id]) ? (answers[id] as string[]) : []);
  const label = (id: string, value: string): string => labelIndex.get(id)?.get(value) ?? value;
  // A draft saved against an older catalog can hold option values that no longer
  // exist. Rendering those raw ("druck") looks like a bug, so unknown values are
  // dropped from the panel rather than passed through.
  const known = (id: string, value: string): boolean => labelIndex.get(id)?.has(value) ?? true;
  const labels = (id: string): string[] =>
    multi(id).filter((value) => known(id, value)).map((value) => label(id, value));

  // ── Zielgruppe ────────────────────────────────────────────────────────────
  const persona: PackageChip[] = [];
  const group = single("nutzergruppe");
  if (group) persona.push(chip(label("nutzergruppe", group)));
  const role = text(answers.nutzerrolle);
  if (role) persona.push(chip(role));
  const literacy = single("datenkompetenz");
  if (literacy) persona.push(chip(`Datenkompetenz: ${label("datenkompetenz", literacy)}`, "plain"));
  const frequency = single("nutzungsfrequenz");
  if (frequency) persona.push(chip(`Nutzung: ${label("nutzungsfrequenz", frequency)}`, "plain"));
  if (single("verwendung") === "extern") persona.push(chip("Auch externe Empfänger", "warn"));
  // The pain points are what the first page has to answer, so they read as
  // problems to solve rather than as neutral attributes.
  for (const entry of labels("herausforderungen")) persona.push(chip(entry, "warn"));
  if (text(answers.fehlt)) persona.push(chip("Lücken beschrieben", "plain"));

  // ── Seitenstruktur ────────────────────────────────────────────────────────
  const pageNames: string[] = [];
  if (single("uebersicht") === "ja") pageNames.push("Übersicht");
  pageNames.push(...labels("themen"));

  const pages: PackageChip[] = pageNames.map((name) => chip(name));
  if (pages.length > 0) {
    const named = splitLines(answers.seitenliste).length;
    pages.push(chip(`${pageNames.length} Seiten${named ? ` · ${named} benannt` : ""}`, "plain"));
  }
  const scope = single("scopeart");
  if (scope) pages.push(chip(label("scopeart", scope), "plain"));

  // ── Visual-Bausteine ──────────────────────────────────────────────────────
  const visuals: PackageChip[] = [];
  const kpis = splitLines(answers.kpiliste);
  if (kpis.length) visuals.push(chip(`${kpis.length} KPI-Kachel${kpis.length === 1 ? "" : "n"}`));

  const comparison = single("vergleich");
  if (comparison === "verlauf" || comparison === "beides") visuals.push(chip("Liniendiagramm (Trend)"));
  if (comparison === "gruppen" || comparison === "beides") visuals.push(chip("Balkendiagramm (Gruppen)"));
  if (single("geo") === "ja") visuals.push(chip("Karten-Visual"));
  if (single("kpiziele") === "ja") visuals.push(chip("Zielerreichung / Ampel", "plain"));
  if (single("drilldown") === "ja") visuals.push(chip("Drill-Through in Details", "plain"));

  const dimensions = labels("dimensionen");
  if (dimensions.length) visuals.push(chip(`Aufschlüsselung: ${dimensions.join(", ")}`, "plain"));

  // ── Datenanbindung ────────────────────────────────────────────────────────
  const sources = splitCommas(answers.berichtquellen).map((entry) => chip(entry));

  const dataRows: DataRow[] = [];
  const sourceCount = single("quellenanzahl");
  const refresh = single("refresh");
  const volume = single("volumen");
  if (sourceCount) dataRows.push({ label: "Quellen", value: label("quellenanzahl", sourceCount) });
  if (refresh) dataRows.push({ label: "Aktualisierung", value: label("refresh", refresh) });
  if (volume) dataRows.push({ label: "Volumen", value: label("volumen", volume) });
  const history = single("historie");
  if (history) dataRows.push({ label: "Historie", value: label("historie", history) });
  const scd = single("historisierung");
  if (scd) dataRows.push({ label: "Stammdaten-Historie", value: label("historisierung", scd) });
  if (refresh || volume) {
    let mode = "Import empfohlen";
    if (refresh === "echtzeit") mode = "DirectQuery / Direct Lake prüfen";
    else if (volume === "sehr_gross") mode = "Import mit Aggregationen oder Direct Lake prüfen";
    dataRows.push({ label: "Speichermodus", value: mode });
  }

  // ── Verteilung & Sichtbarkeit ─────────────────────────────────────────────
  // Nutzungsfrequenz and the internal/external split live in the Zielgruppe
  // block — they describe the audience, not the delivery mechanics.
  const delivery: PackageChip[] = [];
  for (const value of multi("exporte")) {
    if (!known("exporte", value)) continue;
    if (value === "keine") continue;
    delivery.push(chip(label("exporte", value), "plain"));
  }
  const writeback = single("writeback");
  if (writeback === "ja") delivery.push(chip("Writeback", "warn"));

  const rls = single("rls");
  if (rls === "ja") {
    delivery.push(chip("Row-Level Security", "warn"));
    const attribute = text(answers.rlsmerkmal);
    if (attribute) delivery.push(chip(`nach ${attribute}`, "plain"));
  } else if (rls === "nein") {
    delivery.push(chip("Keine RLS nötig", "ok"));
  }
  if (text(answers.sonstiges)) delivery.push(chip("Sonderwünsche notiert", "plain"));

  // ── Aufwandsindikator ─────────────────────────────────────────────────────
  let score = 0;
  let scored = false;

  const sourceCountPoints: Record<string, number> = { "1": 0, "2_3": 1, "4_5": 2, mehr_5: 3 };
  if (sourceCount) {
    score += sourceCountPoints[sourceCount] ?? 0;
    scored = true;
  }

  const volumePoints: Record<string, number> = { ueberschaubar: 0, mittel: 1, gross: 2, sehr_gross: 3 };
  if (volume) {
    score += volumePoints[volume] ?? 0;
    scored = true;
  }

  const refreshPoints: Record<string, number> = {
    monatlich: 0,
    woechentlich: 0,
    taeglich: 1,
    mehrmals_taeglich: 2,
    echtzeit: 3,
  };
  if (refresh) {
    score += refreshPoints[refresh] ?? 0;
    scored = true;
  }

  if (history === "mehr_5") score += 1;
  if (scd === "ja") {
    score += 1;
    scored = true;
  }
  if (rls === "ja") {
    score += 2;
    scored = true;
  }
  const kpiLogic = single("kpilogik");
  if (kpiLogic) {
    if (kpiLogic === "teilweise") score += 1;
    if (kpiLogic === "nein") score += 2;
    scored = true;
  }
  if (writeback === "ja") {
    score += 2;
    scored = true;
  }
  if (single("drilldown") === "ja") score += 1;

  const band: EffortBand | null = !scored ? null : score <= 3 ? 1 : score <= 7 ? 2 : score <= 10 ? 3 : 4;

  return {
    title: text(answers.bericht) || "Live",
    purpose: text(answers.kernfrage),
    persona,
    pages,
    visuals,
    sources,
    dataRows,
    delivery,
    effort: {
      scored,
      band,
      bandName: band ? BAND_NAMES[band - 1] : "—",
      score,
    },
  };
}

/** Memoised view of {@link buildRequirementsPackage} for the form. */
export function useRequirementsPackage(
  sections: DashboardSection[],
  answers: Answers
): RequirementsPackage {
  return useMemo(() => buildRequirementsPackage(sections, answers), [sections, answers]);
}
